/**
 * Routes. Every view is a lazily loaded chunk – the initial bundle only contains the app shell.
 * Records open as child routes of the table route (overlay above the table, own URL): a deep
 * link to /db/x/t/y/r/1 renders the table underneath and the overlay on top.
 */
import { createRouter, createWebHistory, type Router } from 'vue-router';
import { trackLoading } from '../services/notify';
import { useSessionStore } from '../stores/session';
import { authGuard } from './guard';

export function createAppRouter(): Router {
  const router = createRouter({
    history: createWebHistory(),
    routes: [
      { path: '/login', name: 'login', component: () => import('../views/LoginView.vue'), meta: { public: true } },
      {
        path: '/',
        component: () => import('../layouts/AppLayout.vue'),
        children: [
          { path: '', name: 'home', component: () => import('../views/HomeView.vue') },
          { path: 'db/:db', name: 'database', component: () => import('../views/DatabaseView.vue'), props: true },
          {
            path: 'db/:db/t/:table',
            name: 'table',
            component: () => import('../views/TableView.vue'),
            props: true,
            children: [
              { path: 'r/:id', name: 'record', component: () => import('../views/RecordView.vue'), props: true, meta: { overlay: true } },
              { path: 'new', name: 'record-new', component: () => import('../views/RecordView.vue'), props: true, meta: { overlay: true } },
            ],
          },
          // record as its own page (preference show_url = page)
          { path: 'db/:db/t/:table/record/:id', name: 'record-page', component: () => import('../views/RecordView.vue'), props: (r) => ({ ...r.params, layout: 'page' }) },
          { path: 'db/:db/t/:table/record-new', name: 'record-page-new', component: () => import('../views/RecordView.vue'), props: (r) => ({ ...r.params, layout: 'page' }) },
          {
            path: 'account',
            component: () => import('../views/account/AccountLayout.vue'),
            children: [
              { path: '', redirect: { name: 'account-settings' } },
              { path: 'settings', name: 'account-settings', component: () => import('../views/account/SettingsView.vue') },
              { path: 'password', name: 'account-password', component: () => import('../views/account/PasswordView.vue') },
              { path: 'security', name: 'account-security', component: () => import('../views/account/SecurityView.vue') },
            ],
          },
          // print layouts and the print designer (Phase 5d): admins only, lazy
          { path: 'admin/print', name: 'print-layouts', component: () => import('../views/admin/PrintLayoutsView.vue'), meta: { admin: true } },
          { path: 'admin/print/:name', name: 'print-designer', component: () => import('../views/admin/PrintDesignerView.vue'), props: true, meta: { admin: true } },
          // record comparison (6e): legacy parameters as query (links ?form=compare&… resolve here)
          { path: 'compare', name: 'compare', component: () => import('../views/CompareView.vue') },
          {
            path: 'compare/:db/:tableA/:idA/:tableB/:idB',
            redirect: (to) => ({
              path: '/compare',
              query: { ...to.query, db: String(to.params.db), table: `${String(to.params.tableA)},${String(to.params.tableB)}`, records: `${String(to.params.idA)},${String(to.params.idB)}` },
            }),
          },
          // link target of a later phase: legacy custom forms (placeholder)
          { path: 'forms/:name', name: 'legacy-form', component: () => import('../views/PendingView.vue') },
          // developer page (element addons): in production builds for admins only
          { path: 'dev/elements', name: 'dev-elements', component: () => import('../views/DevElementsView.vue'), meta: { admin: !import.meta.env.DEV } },
          { path: ':pathMatch(.*)*', name: 'not-found', component: () => import('../views/NotFoundView.vue') },
        ],
      },
    ],
    scrollBehavior(to, from, saved) {
      if (saved) return saved;
      // opening / closing / switching a record overlay keeps the table's scroll position
      if (to.meta.overlay || from.meta.overlay) return false;
      return { top: 0 };
    },
  });

  let navigating = false;
  router.beforeEach((to) => {
    if (!navigating) trackLoading(1);
    navigating = true;
    return authGuard(to, useSessionStore());
  });
  const done = () => {
    if (navigating) trackLoading(-1);
    navigating = false;
  };
  router.afterEach(done);
  router.onError((err) => {
    done();
    // a lazy chunk vanished after a deploy → reload once to get the new build
    if (/dynamically imported module|Importing a module script failed|Failed to fetch/.test(String((err as Error)?.message))) {
      if (!sessionStorage.getItem('creator.chunkReload')) {
        sessionStorage.setItem('creator.chunkReload', '1');
        window.location.reload();
      }
    }
  });
  router.isReady().then(() => sessionStorage.removeItem('creator.chunkReload'), () => {});
  return router;
}
